import React from 'react'
import { Button } from '@/ui/atoms/Button'
import { Card, CardProps } from '@/ui/components/Card'
import { Icon } from '@/ui/atoms/Icon'
import Link from 'next/link'

export interface LockedPreviewGridProps {
  /**
   * Preview cards to display (with blurred images and minimal data)
   */
  previewCards: CardProps[]
  /**
   * Current path for redirect after login
   */
  currentPath?: string
  /**
   * Number of columns in the grid
   */
  gridCols?: 1 | 2 | 3 | 4
  /**
   * Title for the locked section
   */
  title?: string
  /**
   * Message for the locked section
   */
  message?: string
  /**
   * Label for sign up button
   */
  signUpButtonLabel?: string
  /**
   * Label for sign in button
   */
  signInButtonLabel?: string
}

/**
 * Displays a grid of preview cards with a lock overlay
 * Used for gated content to show that content exists without revealing sensitive data
 */
export const LockedPreviewGrid: React.FC<LockedPreviewGridProps> = ({
  previewCards,
  currentPath,
  gridCols = 1,
  title = 'This content is locked',
  message = 'Sign in to view full details and access this content.',
  signUpButtonLabel = 'Create account',
  signInButtonLabel = 'Sign in',
}) => {
  const signUpUrl = currentPath
    ? `/sign-up?redirect_url=${encodeURIComponent(currentPath)}`
    : '/sign-up'

  const loginUrl = currentPath ? `/login?redirect_url=${encodeURIComponent(currentPath)}` : '/login'

  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[gridCols]

  // Remove duplicates based on image URL
  const seen = new Set<string>()
  const uniqueCards = previewCards.filter((card) => {
    // Type-safe check: only access image if it exists on this card type
    const image = 'image' in card ? card.image : undefined
    const imageKey = typeof image === 'string' ? image : 
                    (image && typeof image === 'object' && 'url' in image) ? String(image.url) :
                    `no-image-${'title' in card ? card.title || 'untitled' : 'untitled'}`
    if (seen.has(imageKey)) return false
    seen.add(imageKey)
    return true
  })

  return (
    <div className="relative">
      {/* Preview Grid with blur overlay */}
      <div className="relative my-14">
        <div className={`grid ${gridColsClass} gap-4`}>
          {uniqueCards.map((card, index) => {
            const image = 'image' in card ? card.image : undefined
            const imageKey = typeof image === 'string' ? image : 
                           (image && typeof image === 'object' && 'url' in image) ? String(image.url) :
                           'no-img'
            return (
              <div key={`${imageKey}-${index}`} className="relative">
                <Card {...card} />
              </div>
            )
          })}
        </div>

        {/* Semi-transparent overlay with lock icon, text, and buttons */}
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
          <div className="max-w-lg text-center space-y-5 px-6">
            <div className="flex justify-center">
              <div className="p-4 bg-bg-subtle-blue rounded-full">
                <Icon name={'lock-fill'} className={'!size-6 text-blue-950'} />
              </div>
            </div>
            {title && <h3 className="text-headline-S-semibold">{title}</h3>}
            {message && <p className="text-body-M text-text-subtle">{message}</p>}
            <div className="flex gap-3 justify-center pt-2">
              <Button asChild variant="primary">
                <Link href={signUpUrl}>{signUpButtonLabel}</Link>
              </Button>
              <Button asChild variant="secondary">
                <Link href={loginUrl}>{signInButtonLabel}</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
